import type { Data } from '@dnd-kit/abstract';
import type { Draggable, Droppable } from '@dnd-kit/dom';
import type { MeetingPublic } from '@repo/types/meetings';
import type { ReactNode } from 'react';

import { DragDropProvider, DragOverlay, PointerSensor } from '@dnd-kit/react';
import { VIEW_KEY } from '@repo/types/enums/viewKey';
import { addMinutes, differenceInMinutes } from 'date-fns';
import { createContext, use, useRef, useState } from 'react';

import { EventItem } from '@/components/features/calendar/EventItem';

// Define the context type
type CalendarDndContextType = {
  activeEvent: MeetingPublic | null;
  activeView: VIEW_KEY | null;
  currentTime: Date | null;
  eventHeight: number | null;
  isMultiDay: boolean;
  multiDayWidth: number | null;
  dragHandlePosition: {
    x?: number;
    y?: number;
    data?: {
      isFirstDay?: boolean;
      isLastDay?: boolean;
    };
  } | null;
};

// Create the context
const CalendarDndContext = createContext<CalendarDndContextType>({
  activeEvent: null,
  activeView: null,
  currentTime: null,
  eventHeight: null,
  isMultiDay: false,
  multiDayWidth: null,
  dragHandlePosition: null,
});

// Hook to use the context
export const useCalendarDnd = () => use(CalendarDndContext);

// Props for the provider
interface CalendarDndProviderProps {
  children: ReactNode;
  onEventUpdate: (event: MeetingPublic) => void;
}

export function CalendarDndProvider({ children, onEventUpdate }: CalendarDndProviderProps) {
  const [activeEvent, setActiveEvent] = useState<MeetingPublic | null>(null);
  const [activeView, setActiveView] = useState<VIEW_KEY | null>(null);
  const [currentTime, setCurrentTime] = useState<Date | null>(null);
  const [eventHeight, setEventHeight] = useState<number | null>(null);
  const [isMultiDay, setIsMultiDay] = useState(false);
  const [multiDayWidth, setMultiDayWidth] = useState<number | null>(null);
  const [dragHandlePosition, setDragHandlePosition] = useState<{
    x?: number;
    y?: number;
    data?: {
      isFirstDay?: boolean;
      isLastDay?: boolean;
    };
  } | null>(null);

  // Store original event dimensions
  const eventDimensions = useRef<{ height: number }>({ height: 0 });

  const handleDragStart = (source: Draggable<Data> | null) => {
    if (!source?.data) {
      console.error('Missing data in drag start event', source);
      return;
    }
    const { event: calendarEvent, view, height, isMultiDay: eventIsMultiDay, multiDayWidth: eventMultiDayWidth, dragHandlePosition: eventDragHandlePosition } = source.data;

    setActiveEvent(calendarEvent);
    setActiveView(view);
    setCurrentTime(new Date(calendarEvent.startDate));
    setIsMultiDay(eventIsMultiDay || false);
    setMultiDayWidth(eventMultiDayWidth || null);
    setDragHandlePosition(eventDragHandlePosition || null);

    if (height) {
      eventDimensions.current.height = height;
      setEventHeight(height);
    }
  };

  const handleDragOver = (target: Droppable<Data> | null, source: Draggable<Data> | null) => {
    if (!source || !target || !activeEvent || !currentTime) return;

    const { date, time } = target.data;

    // Update time for week/day views
    if (time !== undefined && activeView !== VIEW_KEY.MONTH) {
      const newTime = new Date(date);

      // Calculate hours and minutes with 15-minute precision
      const hours = Math.floor(time);
      const fractionalHour = time - hours;

      // Map to nearest 15 minute interval (0, 0.25, 0.5, 0.75)
      let minutes = 0;
      if (fractionalHour < 0.125) minutes = 0;
      else if (fractionalHour < 0.375) minutes = 15;
      else if (fractionalHour < 0.625) minutes = 30;
      else minutes = 45;

      newTime.setHours(hours, minutes, 0, 0);
      setCurrentTime(newTime);
    } else if (activeView === VIEW_KEY.MONTH) {
      const newTime = new Date(date);
      setCurrentTime(newTime);
    }
  };

  const handleDragEnd = (target: Droppable<Data> | null, source: Draggable<Data> | null) => {
    if (!target || !source || !activeEvent || !currentTime) {
      setActiveEvent(null);
      setActiveView(null);
      setCurrentTime(null);
      setEventHeight(null);
      setIsMultiDay(false);
      setMultiDayWidth(null);
      setDragHandlePosition(null);
      return;
    }

    try {
      const activeData = source.data;
      const overData = target.data;
      // Verify we have all required data
      if (!activeData.event || !overData.date) {
        throw new Error('Missing required event data');
      }

      const calendarEvent = activeData.event;
      const date = overData.date;
      const time = overData.time;

      // Calculate new start time
      const newStart = new Date(date);
      const originalStart = new Date(calendarEvent.startDate);

      // If time is provided (for week/day views), set the hours and minutes
      if (time !== undefined) {
        const hours = Math.floor(time);
        const fractionalHour = time - hours;

        // Map to nearest 15 minute interval (0, 0.25, 0.5, 0.75)
        let minutes = 0;
        if (fractionalHour < 0.125) minutes = 0;
        else if (fractionalHour < 0.375) minutes = 15;
        else if (fractionalHour < 0.625) minutes = 30;
        else minutes = 45;

        newStart.setHours(hours, minutes, 0, 0);
      } else {
        // For month view, preserve the original time from the event's startDate
        newStart.setHours(originalStart.getHours(), originalStart.getMinutes(), originalStart.getSeconds(), originalStart.getMilliseconds());
      }

      // Calculate new end time based on the original duration
      const originalEnd = new Date(calendarEvent.endDate);
      const durationMinutes = differenceInMinutes(originalEnd, originalStart);
      const newEnd = addMinutes(newStart, durationMinutes);

      // Only update if the start time has actually changed
      const hasStartTimeChanged =
        originalStart.getFullYear() !== newStart.getFullYear() ||
        originalStart.getMonth() !== newStart.getMonth() ||
        originalStart.getDate() !== newStart.getDate() ||
        originalStart.getHours() !== newStart.getHours() ||
        originalStart.getMinutes() !== newStart.getMinutes();

      if (hasStartTimeChanged) {
        onEventUpdate({
          ...calendarEvent,
          startDate: newStart.toISOString(),
          endDate: newEnd.toISOString(),
        });
      }
    } catch (error) {
      console.error('Error in drag end handler:', error);
    } finally {
      setActiveEvent(null);
      setActiveView(null);
      setCurrentTime(null);
      setEventHeight(null);
      setIsMultiDay(false);
      setMultiDayWidth(null);
      setDragHandlePosition(null);
    }
  };

  return (
    <DragDropProvider
      sensors={[PointerSensor]}
      onDragStart={(event) => {
        handleDragStart(event.operation.source);
      }}
      onDragOver={(event) => {
        const { target, source } = event.operation;
        handleDragOver(target, source);
      }}
      onDragEnd={(event) => {
        const { target, source } = event.operation;
        const { canceled } = event;
        if (canceled) return;
        handleDragEnd(target, source);
      }}
    >
      <CalendarDndContext
        value={{
          activeEvent,
          activeView,
          currentTime,
          eventHeight,
          isMultiDay,
          multiDayWidth,
          dragHandlePosition,
        }}
      >
        {children}

        <DragOverlay>
          {() => {
            if (!activeEvent || !activeView) return null;
            return (
              <div
                style={{
                  height: eventHeight ? `${eventHeight}px` : 'auto',
                  width: isMultiDay && multiDayWidth ? `${multiDayWidth}%` : '100%',
                }}
              >
                <EventItem
                  event={activeEvent}
                  view={activeView}
                  isDragging={true}
                  showTime={activeView !== VIEW_KEY.MONTH}
                  currentTime={currentTime || undefined}
                  isFirstDay={dragHandlePosition?.data?.isFirstDay !== false}
                  isLastDay={dragHandlePosition?.data?.isLastDay !== false}
                  className="opacity-60 shadow-lg"
                />
              </div>
            );
          }}
        </DragOverlay>
      </CalendarDndContext>
    </DragDropProvider>
  );
}
